export class TicketAvailabilityCalculator {
  constructor(data) {
    this.revealedHold = data.revealedHold;
    this.buildNodeGraph(data.groups || [], data.ticketTypes || []);
  }

  heldForTicketType(ticketTypeId) {
    const hold = this.revealedHold;
    if (!hold) return null;
    const items = hold.items || (hold.ticket_type_id ? [{ ticket_type_id: hold.ticket_type_id, quantity: hold.quantity }] : []);
    const item = items.find(candidate => candidate.ticket_type_id === ticketTypeId);
    return item ? item.quantity : null;
  }

  buildNodeGraph(groups, ticketTypes) {
    this.groupMap = new Map();
    this.ticketTypeMap = new Map();
    
    groups.forEach(group => {
      this.groupMap.set(group.id, {
        ...group,
        parent: null,
        children: [],
        cartQuantity: 0,
        consumedHolds: 0,
        baseAvailable: null
      });
    });

    groups.forEach(group => {
      const node = this.groupMap.get(group.id);
      if (group.parent_id) {
        const parentNode = this.groupMap.get(group.parent_id);
        if (parentNode) {
          node.parent = parentNode;
          parentNode.children.push(node);
        }
      }
      
      const sold = group.sold_count || 0;
      const reserved = group.reserved_count || 0;
      const held = group.held_count || 0;
      const capacity = group.capacity || 0;
      node.baseAvailable = Math.max(0, capacity - sold - reserved - held);
      node.available = node.baseAvailable;
    });

    ticketTypes.forEach(ticketType => {
      const held = this.heldForTicketType(ticketType.id);
      const ttWithRevealedHold = held !== null
        ? { ...ticketType, revealed_held_count: held }
        : ticketType;
      
      this.ticketTypeMap.set(ttWithRevealedHold.id, {
        ...ttWithRevealedHold,
        groupNode: this.groupMap.get(ttWithRevealedHold.ticket_group_id)
      });
    });
  }

  calculateEffectiveSellable(node, revealedHeldCount = 0, adjustments = {}) {
    const { cartAdjustment = 0, consumedHoldsAdjustment = 0 } = adjustments;

    const available = node.capacity || node.available || 0;
    const sold = node.sold_count || 0;
    const reserved = node.reserved_count || 0;
    const held = node.held_count || 0;
    const revealed = revealedHeldCount || node.revealed_held_count || 0;

    const nonHeldCommitted = sold + reserved + cartAdjustment;
    const physicalRemaining = available - nonHeldCommitted;
    
    const adjustedHeld = held - consumedHoldsAdjustment;
    const effectiveHeld = Math.min(adjustedHeld, physicalRemaining);
    const effectiveRevealed = Math.min(revealed, effectiveHeld);
    const unrevealedHeld = effectiveHeld - effectiveRevealed;
    
    const sellableQuantity = physicalRemaining - unrevealedHeld;
    
    return Math.max(sellableQuantity, 0);
  }

  sellableCount(ticketTypeId, options = {}) {
    const { ignoreCart = false } = options;
    const ticketTypeNode = this.ticketTypeMap.get(ticketTypeId);
    if (!ticketTypeNode) {
      return 0;
    }

    const ticketType = ticketTypeNode;
    const revealedHeldCount = ticketType.revealed_held_count || 0;
    const isUnmanifested = ticketType.manifested === false;
    
    const ownSellable = this.calculateEffectiveSellable(ticketType);
    
    if (isUnmanifested) {
      return Math.max(0, ownSellable);
    }

    let currentGroup = ticketTypeNode.groupNode;
    if (!currentGroup) {
      return Math.max(0, ownSellable);
    }

    let minConstraint = ownSellable;
    
    const currentTicketTypeCartQuantity = ignoreCart ? 0 : (ticketTypeNode.cartQuantity || 0);
    const currentTicketTypeConsumedHolds = Math.min(currentTicketTypeCartQuantity, revealedHeldCount);

    while (currentGroup) {
      const groupCartQuantity = ignoreCart ? 0 : (currentGroup.cartQuantity || 0);
      const groupConsumedHolds = ignoreCart ? 0 : (currentGroup.consumedHolds || 0);
      
      const groupSellable = this.calculateEffectiveSellable(
        currentGroup,
        revealedHeldCount,
        {
          cartAdjustment: groupCartQuantity - currentTicketTypeCartQuantity,
          consumedHoldsAdjustment: groupConsumedHolds - currentTicketTypeConsumedHolds
        }
      );
      
      minConstraint = Math.min(minConstraint, groupSellable);
      currentGroup = currentGroup.parent;
    }

    return Math.max(0, minConstraint);
  }

  calculate() {
    const ticketTypesWithAvailability = Array.from(this.ticketTypeMap.values()).map(ticketType => {
      const held = this.heldForTicketType(ticketType.id);
      const holdCap = this.revealedHold && this.revealedHold.capped_by_hold && held !== null
        ? held
        : Infinity;
      const available = Math.min(this.sellableCount(ticketType.id), holdCap);
      const soldOut = available <= 0;
      const quantityOptions = ticketType.quantity_options || [];
      const availableQuantityOptions = quantityOptions.filter(qty => qty <= available);

      return {
        ...ticketType,
        quantity: ticketType.cartQuantity,
        available_tickets: available,
        sold_out: soldOut,
        available_quantity_options: availableQuantityOptions
      };
    });

    const overallSoldOut = ticketTypesWithAvailability.length === 0 || 
      ticketTypesWithAvailability.every(tt => tt.sold_out);

    return {
      ticketTypes: ticketTypesWithAvailability,
      soldOut: overallSoldOut
    };
  }

  updateCart({ ticketTypeId, quantity }) {
    const ticketTypeNode = this.ticketTypeMap.get(ticketTypeId);
    if (!ticketTypeNode) {
      return;
    }

    const isUnmanifested = ticketTypeNode.manifested === false;
    if (isUnmanifested) {
      ticketTypeNode.cartQuantity = quantity;
      return;
    }

    const oldCartQuantity = ticketTypeNode.cartQuantity || 0;
    const newCartQuantity = quantity;
    const cartQuantityDiff = newCartQuantity - oldCartQuantity;

    const revealedHeldCount = ticketTypeNode.revealed_held_count || 0;
    const oldConsumedHolds = Math.min(oldCartQuantity, revealedHeldCount);
    const newConsumedHolds = Math.min(newCartQuantity, revealedHeldCount);
    const consumedHoldsDiff = newConsumedHolds - oldConsumedHolds;

    ticketTypeNode.cartQuantity = newCartQuantity;

    let currentGroup = ticketTypeNode.groupNode;
    while (currentGroup) {
      currentGroup.cartQuantity += cartQuantityDiff;
      currentGroup.consumedHolds += consumedHoldsDiff;
      currentGroup.available = Math.max(0, currentGroup.baseAvailable - currentGroup.cartQuantity);
      currentGroup = currentGroup.parent;
    }
  }

  groupSellableCount(groupNode, revealedHeldCount = 0) {
    return this.calculateEffectiveSellable(groupNode, revealedHeldCount);
  }

  getRevealedHeldCountForGroup(ticketTypesInGroup) {
    return ticketTypesInGroup.reduce((sum, tt) => sum + Math.min(tt.cartQuantity || 0, tt.revealed_held_count || 0), 0);
  }

  collectGroupsWithCartItems() {
    const groupsMap = new Map();

    this.ticketTypeMap.forEach(ticketType => {
      if (ticketType.manifested === false) return;

      const groupNode = ticketType.groupNode;
      if (!groupNode) return;

      if (!groupsMap.has(groupNode.id)) {
        groupsMap.set(groupNode.id, {
          groupNode,
          ticketTypes: [],
          totalCart: 0,
          hasRevealedHold: false
        });
      }

      const groupInfo = groupsMap.get(groupNode.id);
      if ((ticketType.cartQuantity || 0) > 0) {
        groupInfo.ticketTypes.push(ticketType);
        groupInfo.totalCart += ticketType.cartQuantity || 0;
      }
      groupInfo.hasRevealedHold = groupInfo.hasRevealedHold || (ticketType.revealed_held_count || 0) > 0;
    });

    return Array.from(groupsMap.values()).filter(g => g.ticketTypes.length > 0);
  }

  findMinSellableForGroup(groupInfo) {
    const { groupNode, ticketTypes, totalCart } = groupInfo;
    const revealedHeldCount = this.getRevealedHeldCountForGroup(ticketTypes);

    let minSellable = this.groupSellableCount(groupNode, revealedHeldCount);

    let currentGroup = groupNode.parent;
    while (currentGroup) {
      const parentSellable = this.groupSellableCount(currentGroup, revealedHeldCount);
      const siblingCart = (currentGroup.cartQuantity || 0) - totalCart;
      const availableForThisGroup = Math.max(0, parentSellable - siblingCart);

      minSellable = Math.min(minSellable, availableForThisGroup);
      currentGroup = currentGroup.parent;
    }

    return minSellable;
  }

  allocateProportionally(ticketTypes, available, adjustments) {
    const totalCart = ticketTypes.reduce((sum, tt) => sum + (tt.cartQuantity || 0), 0);

    const allocations = ticketTypes.map(tt => {
      const currentCart = tt.cartQuantity || 0;
      const proportion = currentCart / totalCart;
      const exactAllocation = available * proportion;
      const baseAllocation = Math.floor(exactAllocation);
      const remainder = exactAllocation - baseAllocation;

      return { ticketType: tt, currentCart, baseAllocation, remainder };
    });

    allocations.sort((a, b) => b.remainder - a.remainder);

    const allocatedTotal = allocations.reduce((sum, a) => sum + a.baseAllocation, 0);
    const remainingToAllocate = available - allocatedTotal;

    allocations.forEach((allocation, index) => {
      const extraTicket = index < remainingToAllocate ? 1 : 0;
      const newQuantity = allocation.baseAllocation + extraTicket;

      this.updateCart({ ticketTypeId: allocation.ticketType.id, quantity: newQuantity });
      adjustments.push({
        id: allocation.ticketType.id,
        name: allocation.ticketType.name,
        oldQuantity: allocation.currentCart,
        newQuantity
      });
    });
  }

  reconcileCart() {
    const adjustments = [];

    this.ticketTypeMap.forEach((ticketTypeNode) => {
      const oldQuantity = ticketTypeNode.cartQuantity || 0;
      if (ticketTypeNode.sales_paused && oldQuantity > 0) {
        this.updateCart({ ticketTypeId: ticketTypeNode.id, quantity: 0 });
        adjustments.push({ id: ticketTypeNode.id, name: ticketTypeNode.name, oldQuantity, newQuantity: 0 });
      }
    });

    const groupsToProcess = this.collectGroupsWithCartItems();

    groupsToProcess.sort((a, b) => {
      if (a.hasRevealedHold && !b.hasRevealedHold) return 1;
      if (!a.hasRevealedHold && b.hasRevealedHold) return -1;
      return this.findMinSellableForGroup(a) - this.findMinSellableForGroup(b);
    });

    groupsToProcess.forEach(groupInfo => {
      const minSellable = this.findMinSellableForGroup(groupInfo);

      if (groupInfo.totalCart > minSellable) {
        this.allocateProportionally(groupInfo.ticketTypes, minSellable, adjustments);
      }
    });

    return adjustments;
  }

  updateSaleWindows(ticketTypes) {
    ticketTypes.forEach(tt => {
      const existingNode = this.ticketTypeMap.get(tt.id);
      if (existingNode) {
        existingNode.scheduled = tt.scheduled;
        existingNode.opens_later = tt.opens_later;
        existingNode.start_time = tt.start_time;
        existingNode.human_start_time = tt.human_start_time;
        existingNode.human_sales_start_date = tt.human_sales_start_date;
      }
    });
  }

  setRevealedHold(revealedHold) {
    this.revealedHold = revealedHold;
    this.ticketTypeMap.forEach((node) => {
      const cartQuantity = node.cartQuantity || 0;
      const oldConsumedHolds = Math.min(cartQuantity, node.revealed_held_count || 0);
      node.revealed_held_count = this.heldForTicketType(node.id) || 0;
      const consumedHoldsDiff = Math.min(cartQuantity, node.revealed_held_count) - oldConsumedHolds;

      let currentGroup = node.groupNode;
      while (consumedHoldsDiff !== 0 && currentGroup) {
        currentGroup.consumedHolds += consumedHoldsDiff;
        currentGroup = currentGroup.parent;
      }
    });
  }

  updateGroupsAndTicketTypes(data) {
    data.ticketTypes.forEach(tt => {
      const existingNode = this.ticketTypeMap.get(tt.id);
      if (existingNode) {
        existingNode.sold_count = tt.sold_count;
        existingNode.reserved_count = tt.reserved_count;
        existingNode.held_count = tt.held_count;
        existingNode.available = tt.available;
        existingNode.sales_paused = tt.sales_paused;
      }
    });

    data.groups.forEach(group => {
      const existingNode = this.groupMap.get(group.id);
      if (existingNode) {
        existingNode.sold_count = group.sold_count;
        existingNode.reserved_count = group.reserved_count;
        existingNode.held_count = group.held_count;
        existingNode.capacity = group.capacity;
        
        const sold = group.sold_count || 0;
        const reserved = group.reserved_count || 0;
        const held = group.held_count || 0;
        const capacity = group.capacity || 0;
        existingNode.baseAvailable = Math.max(0, capacity - sold - reserved - held);
        existingNode.available = Math.max(0, existingNode.baseAvailable - existingNode.cartQuantity);
      }
    });
  }

}
